import type { DataTextureImageData, Texture } from '@seddi/three';
import pica, { type Pica, type PicaResizeOptions } from 'pica';
import type { TextureImage } from '../three/materials/materialTypes';

const picaOnce = (() => {
  let picaInstance: Pica | null = null;
  return () => {
    if (picaInstance === null) {
      picaInstance = pica();
    }
    return picaInstance;
  };
})();

/**
 * Get pixels in CPU.
 * @param texture Texture.
 */
const getImageBitmapData = (texture: Texture<ImageBitmap>): ImageData => {
  // This is the only way, right now, to get the data in the CPU for an imported image
  // https://stackoverflow.com/questions/68102995/is-it-possible-to-get-pixels-from-imagebitmap-web-object-without-a-canvas
  // The recommended way is to render it on a canvas
  // https://developer.mozilla.org/en-US/docs/Web/API/ImageBitmap

  const canvas = document.createElement('canvas');
  canvas.height = texture.image.height;
  canvas.width = texture.image.width;

  const context = canvas.getContext('2d');
  context.drawImage(texture.image, 0, 0, canvas.width, canvas.height);
  const data = context.getImageData(0, 0, canvas.width, canvas.height);
  return data;
};

const isDataTexture = (image: TextureImage): boolean => {
  return (image as DataTextureImageData).data != undefined;
};

const toImageDataArray = (elements: Iterable<number>): ImageDataArray => {
  return Uint8ClampedArray.from(elements);
};

/**
 * Generate high-quality mipmaps.
 * @param texture Renderer texture.
 * @param tags Texture tags.
 */
export const generateTextureMips = async (texture: Texture<TextureImage>, tags?: string[]) => {
  let imageData!: ImageData;
  {
    const { image } = texture;
    const isDataImage = isDataTexture(image);
    const data: ImageDataArray = !isDataImage
      ? getImageBitmapData(texture as Texture<ImageBitmap>).data
      : toImageDataArray((image as DataTextureImageData).data);
    const { height, width } = image;
    imageData = new ImageData(data, width, height);
  }

  // Hamming
  // {
  //   texture.mipmaps = await generatePicaMips(imageData, { filter: 'hamming' });
  // }
  // Lanczos, win = 2
  // {
  //   texture.mipmaps = await generatePicaMips(imageData, { filter: 'lanczos2' });
  // }
  // Magic Kernel Sharp 2013, win = 2.5
  {
    texture.mipmaps = await generatePicaMips(imageData, { filter: 'mks2013' });
  }

  texture.generateMipmaps = false;
};

const generatePicaMips = async (
  image: ImageData,
  config: PicaResizeOptions = {},
): Promise<ImageData[]> => {
  const picaInstance = picaOnce();
  const mips: ImageData[] = [image];
  let currentImageData = image;

  while (currentImageData.width > 1 || currentImageData.height > 1) {
    const fromCanvas = document.createElement('canvas');
    fromCanvas.width = currentImageData.width;
    fromCanvas.height = currentImageData.height;
    const fromCtx = fromCanvas.getContext('2d');
    if (!fromCtx) throw new Error('Could not get 2d context from canvas');
    fromCtx.putImageData(currentImageData, 0, 0);

    const newWidth = Math.max(1, Math.floor(currentImageData.width / 2));
    const newHeight = Math.max(1, Math.floor(currentImageData.height / 2));

    const toCanvas = document.createElement('canvas');
    toCanvas.width = newWidth;
    toCanvas.height = newHeight;

    await picaInstance.resize(fromCanvas, toCanvas, config);

    const toCtx = toCanvas.getContext('2d');
    if (!toCtx) throw new Error('Could not get 2d context from canvas');

    const newImageData = toCtx.getImageData(0, 0, newWidth, newHeight);
    mips.push(newImageData);
    currentImageData = newImageData;
  }

  return mips;
};
